import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { PinPad } from '@renderer/components/PinPad';
import type { UsuarioVista } from '@shared/types';
import { DESCRIPCION_ROL, NOMBRE_ROL, ROLES_ASIGNABLES, type Rol } from '@shared/permisos';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

interface Props {
  abierto: boolean;
  usuario: UsuarioVista | null;
  onClose: () => void;
  onSaved: () => void;
}

type Etapa = 'datos' | 'pin' | 'pin_confirmacion';

export function FormUsuario({ abierto, usuario, onClose, onSaved }: Props) {
  const [nombre, setNombre] = useState('');
  const [rol, setRol] = useState<Rol>('mesero');
  const [activo, setActivo] = useState(true);
  // Sprint 12.1b — activoLogin=false para empleados que NO usan el sistema (cocineros).
  const [activoLogin, setActivoLogin] = useState(true);
  /** Sprint 94 — Cómo se presenta. No es el rol y no cambia ni un permiso. */
  const [puesto, setPuesto] = useState('');
  const [pin, setPin] = useState<string>('');
  const [, setPinConfirmacion] = useState<string>('');
  const [etapa, setEtapa] = useState<Etapa>('datos');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [resetSignal, setResetSignal] = useState(0);
  const [cambiarPin, setCambiarPin] = useState(false);

  useEffect(() => {
    if (usuario) {
      setNombre(usuario.nombre);
      setRol(usuario.rol);
      setActivo(usuario.activo);
      setActivoLogin(usuario.activoLogin ?? true);
      setPuesto(usuario.puesto ?? '');
    } else {
      setNombre('');
      setRol('mesero');
      setActivo(true);
      setActivoLogin(true);
      setPuesto('');
    }
    setPin('');
    setPinConfirmacion('');
    setEtapa('datos');
    setError(null);
    setCambiarPin(false);
  }, [usuario, abierto]);

  if (!abierto) return null;

  const submitDatos = async () => {
    if (nombre.trim().length < 2) {
      setError('El nombre debe tener al menos 2 caracteres');
      return;
    }
    setError(null);

    /**
     * Sprint 64 — **Todo usuario nuevo lleva PIN**, no sólo el dueño.
     *
     * Antes un mesero se creaba sin credencial y entraba tocando su nombre. En la web eso
     * es un bypass, así que estaba bloqueado: el negocio creó cajera y cocina y ninguna
     * podía entrar. El dueño asigna el PIN inicial —es quien reparte las llaves— y cada
     * quien lo cambia después con el suyo.
     */
    if (!usuario) {
      setEtapa('pin');
      return;
    }
    if (usuario && cambiarPin) {
      setEtapa('pin');
      return;
    }

    // Sin PIN — guardar
    setEnviando(true);
    if (usuario) {
      const res = await window.api.usuarios.actualizar(usuario.id, {
        nombre: nombre.trim(),
        activo,
        activoLogin: rol === 'dueño' ? true : activoLogin,
        // Vacío se guarda como null: significa "muéstrame por mi rol".
        puesto: puesto.trim() || null,
      });
      setEnviando(false);
      if (res.ok) onSaved();
      else setError(traducirErrorBackend(res.error, res.codigo));
    } else {
      const res = await window.api.usuarios.crear({
        nombre: nombre.trim(),
        rol,
        activoLogin: rol === 'dueño' ? true : activoLogin,
      });
      setEnviando(false);
      if (res.ok) onSaved();
      else setError(traducirErrorBackend(res.error, res.codigo));
    }
  };

  const onPinComplete = (valor: string) => {
    if (etapa === 'pin') {
      setPin(valor);
      setResetSignal((v) => v + 1);
      setEtapa('pin_confirmacion');
    } else if (etapa === 'pin_confirmacion') {
      setPinConfirmacion(valor);
      finalizar(valor);
    }
  };

  const finalizar = async (pinConfirmado: string) => {
    if (pin !== pinConfirmado) {
      setError('Los PIN no coinciden');
      setEtapa('pin');
      setPin('');
      setResetSignal((v) => v + 1);
      return;
    }
    setEnviando(true);
    setError(null);
    const res = usuario
      ? await window.api.usuarios.actualizar(usuario.id, {
          nombre: nombre.trim(),
          activo,
          // Sprint 64.1 — Faltaba: cambiar el PIN y el acceso a la vez perdía el acceso.
          activoLogin: usuario.rol === 'dueño' ? true : activoLogin,
          pin,
        })
      : await window.api.usuarios.crear({
          nombre: nombre.trim(),
          rol,
          pin,
          activoLogin: rol === 'dueño' ? true : activoLogin,
        });
    setEnviando(false);
    if (res.ok) onSaved();
    else {
      setError(traducirErrorBackend(res.error, res.codigo));
      setEtapa('datos');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold">{usuario ? 'Editar usuario' : 'Nuevo usuario'}</h2>
          <button onClick={onClose} className="btn-ghost p-2" aria-label="Cerrar" disabled={enviando}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {etapa === 'datos' && (
          <div className="space-y-4 px-6 py-5">
            <div>
              <label className="label" htmlFor="usrNombre">Nombre *</label>
              <input
                id="usrNombre"
                className="input"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                autoFocus
              />
            </div>

            {/*
              Sprint 94 — El puesto, separado del rol.

              Nace de un caso real: quien da soporte al sistema necesita permisos de dueño y **no
              es el dueño del negocio**; verlo como "Dueño" en la lista de la dueña es sencillamente
              falso. La tentación fácil era renombrar la etiqueta del rol, y habría salido caro —
              la dueña también es `dueño`, así que se habría visto a sí misma como consultora.

              Se aclara aquí mismo que no cambia permisos: un campo junto al rol que pareciera
              cambiarlo sería peor que no tenerlo.
            */}
            <div>
              <label className="label" htmlFor="usrPuesto">
                Puesto <span className="font-normal text-slate-500">(opcional)</span>
              </label>
              <input
                id="usrPuesto"
                className="input"
                value={puesto}
                onChange={(e) => setPuesto(e.target.value)}
                maxLength={40}
                placeholder="Consultor TI, Encargada, Socio…"
              />
              <p className="mt-1 text-xs text-slate-500">
                Sólo cambia cómo se ve en las listas. <b>No cambia lo que puede hacer</b> — eso lo
                decide el rol de abajo.
              </p>
            </div>

            {/*
              Sprint 64 — También al EDITAR. Antes el rol sólo se elegía al crear, así que
              "Clau Cajera" quedó como mesero para siempre y no había forma de corregirlo:
              el negocio tuvo que crear usuarios nuevos para algo que era un cambio de
              puesto.
            */}
            {(
              <div>
                <div className="label">¿Qué hace esta persona?</div>
                {/*
                  Sprint 63 (Fase 5) — Cuatro oficios, cada uno con lo que hace escrito
                  al lado. Antes eran dos radios ("Mesero / empleado" y "Dueño (con PIN)")
                  y elegir mal no tenía consecuencia visible; ahora cada oficio ve un
                  sistema distinto, así que hay que poder decidir leyendo.
                */}
                <div className="space-y-2">
                  {ROLES_ASIGNABLES.map((r) => (
                    <label
                      key={r}
                      className={`flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg border-2 px-3 py-2 ${
                        rol === r ? 'border-primary bg-primary/5' : 'border-slate-200'
                      }`}
                    >
                      <input
                        type="radio"
                        className="mt-1"
                        checked={rol === r}
                        onChange={() => setRol(r)}
                      />
                      <span>
                        <span className="block text-sm font-semibold text-slate-800">
                          {NOMBRE_ROL[r]}
                        </span>
                        <span className="block text-xs text-slate-600">{DESCRIPCION_ROL[r]}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/*
              Sprint 12.1b — Acceso al sistema: para el empleado que entra al reparto de
              propinas pero no usa el POS.

              Sprint 64.1 — Aplica a cualquier oficio salvo el dueño. Estaba limitado a
              `mesero`, resto del mundo de dos roles, y el caso que describe el propio texto
              —cocineros, lavaplatos— es el oficio **cocina**, donde no se podía marcar.
            */}
            {rol !== 'dueño' && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={activoLogin}
                    onChange={(e) => setActivoLogin(e.target.checked)}
                  />
                  <span className="text-sm">
                    <span className="font-medium">Puede entrar al sistema</span>
                    <span className="block text-xs text-slate-500 mt-0.5">
                      Desmarca para cocineros, lavaplatos o cualquier empleado que NO use el POS pero
                      sí participe en el reparto de propinas.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {usuario && (
              <>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={activo}
                    onChange={(e) => setActivo(e.target.checked)}
                  />
                  <span>Activo</span>
                </label>
                {/*
                  Sprint 64.1 — Al derecho. Antes esta casilla salía SÓLO para el dueño, que es
                  justo el caso que la auditoría cerró del lado del servidor (cambiar el PIN de
                  un dueño por aquí no pedía el PIN actual ni dejaba rastro). Y no salía para
                  los empleados, que son quienes no tienen PIN y a quienes el aviso de la lista
                  manda a "abrirlos con el lápiz y ponerles uno".
                */}
                {usuario.rol !== 'dueño' && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cambiarPin}
                      onChange={(e) => setCambiarPin(e.target.checked)}
                    />
                    <span>
                      {usuario.tienePin === false
                        ? 'Asignarle un PIN para que pueda entrar'
                        : 'Cambiarle el PIN'}
                    </span>
                  </label>
                )}
                {usuario.rol === 'dueño' && (
                  <p className="text-xs text-slate-500">
                    El PIN de un dueño se cambia desde <strong>Mi PIN</strong>, arriba a la
                    derecha: ahí se pide el PIN actual.
                  </p>
                )}
              </>
            )}

            {error && <div className="error-text">{error}</div>}

            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="btn-ghost" disabled={enviando}>Cancelar</button>
              <button onClick={submitDatos} className="btn-primary" disabled={enviando}>
                {usuario && !cambiarPin ? 'Guardar' : 'Continuar'}
              </button>
            </div>
          </div>
        )}

        {(etapa === 'pin' || etapa === 'pin_confirmacion') && (
          <div className="flex flex-col items-center gap-4 px-6 py-5">
            <PinPad
              label={etapa === 'pin' ? 'Crea el PIN de 4 dígitos' : 'Confirma el PIN'}
              onComplete={onPinComplete}
              error={error ?? undefined}
              resetSignal={resetSignal}
            />
            <button
              onClick={() => {
                setEtapa('datos');
                setPin('');
                setPinConfirmacion('');
                setError(null);
              }}
              className="btn-ghost text-sm"
              disabled={enviando}
            >
              Cancelar PIN
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
